import type { AnalyticsEventPayload, AnalyticsProvider } from '@/services/analytics';
import { AnalyticsEvents, type AnalyticsEventName } from '@/services/analytics/events';
import type { ICookieConsent } from '@/utils/cookieConsent.utils';
import { loadScript } from '@/utils/script.utils';

interface ITikTokPixel extends Array<unknown[]> {
  methods: readonly string[];
  setAndDefer(target: unknown[][], method: string): void;
  instance(pixelId: string): unknown[][];
  page(): void;
  track(event: string, properties: Record<string, unknown>, options?: { event_id: string }): void;
  grantConsent(): void;
  revokeConsent(): void;
  _i?: Record<string, unknown[][]>;
  _t?: Record<string, number>;
  _o?: Record<string, object>;
}

declare global {
  interface Window {
    TiktokAnalyticsObject?: string;
    ttq?: ITikTokPixel;
  }
}

// The server's Events API uses the same pixel (Conversions.TikTok.PixelCode); TikTok deduplicates only within one pixel.
const TIKTOK_PIXEL_ID = 'DBAROCRC77UAEK31DDS0';
const TIKTOK_PIXEL_SCRIPT_SRC = 'https://analytics.tiktok.com/i18n/pixel/events.js';
const TIKTOK_PIXEL_METHODS = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
  'holdConsent',
  'revokeConsent',
  'grantConsent',
];

/** Maps internal analytics event names to TikTok standard events. */
const EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  [AnalyticsEvents.auth.sign_up]: 'CompleteRegistration',
  [AnalyticsEvents.checkout.purchase]: 'Purchase',
  [AnalyticsEvents.billing.trialStarted]: 'StartTrial',
  [AnalyticsEvents.checkout.submit]: 'InitiateCheckout',
};

/** TikTok's base code: a queue whose methods record their calls until the SDK has loaded and replays them. */
function createTikTokPixelQueue(): ITikTokPixel {
  const setAndDefer = (target: unknown[][], method: string): void => {
    Reflect.set(target, method, (...args: unknown[]) => target.push([method, ...args]));
  };
  const ttq = Object.assign([], {
    methods: TIKTOK_PIXEL_METHODS,
    setAndDefer,
    instance: (pixelId: string) => {
      const instance = ttq._i?.[pixelId] ?? [];
      for (const method of TIKTOK_PIXEL_METHODS) setAndDefer(instance, method);
      return instance;
    },
  }) as unknown as ITikTokPixel;
  for (const method of TIKTOK_PIXEL_METHODS) setAndDefer(ttq, method);
  return ttq;
}

export class TikTokPixelAnalytics implements AnalyticsProvider {
  public readonly name = 'tiktok-pixel';
  public readonly consentCategories = ['marketing'] as const;

  public init(): Promise<void> {
    window.TiktokAnalyticsObject = 'ttq';
    const ttq = (window.ttq ??= createTikTokPixelQueue());
    // What the base code's ttq.load does, with the script added through the shared loader.
    ttq._i = { ...ttq._i, [TIKTOK_PIXEL_ID]: Object.assign([], { _u: TIKTOK_PIXEL_SCRIPT_SRC }) };
    ttq._t = { ...ttq._t, [TIKTOK_PIXEL_ID]: Date.now() };
    ttq._o = { ...ttq._o, [TIKTOK_PIXEL_ID]: {} };
    return loadScript(`${TIKTOK_PIXEL_SCRIPT_SRC}?sdkid=${TIKTOK_PIXEL_ID}&lib=ttq`);
  }

  public setConsent(consent: ICookieConsent | null): void {
    if (consent?.marketing) window.ttq?.grantConsent();
    else window.ttq?.revokeConsent();
  }

  public trackPageview(): void {
    window.ttq?.page();
  }

  public trackEvent(name: AnalyticsEventName, payload?: AnalyticsEventPayload): void {
    const tiktokEvent = EVENT_MAP[name];
    if (!tiktokEvent) return;
    const properties = {
      ...(typeof payload?.value === 'number' ? { value: payload.value } : {}),
      ...(typeof payload?.currency === 'string' ? { currency: payload.currency } : {}),
    };
    // The purchase's order id doubles as event id, so TikTok deduplicates it with the server's Events API event.
    const eventId = name === AnalyticsEvents.checkout.purchase ? payload?.transaction_id : undefined;
    if (typeof eventId === 'string') window.ttq?.track(tiktokEvent, properties, { event_id: eventId });
    else window.ttq?.track(tiktokEvent, properties);
  }
}
